import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowRight, Info, Mail } from "lucide-react";
import { CustomFieldRow } from "./CustomFieldRow";
import type { FieldOption } from "./CustomFieldRow";
import { isOwnerFieldType, type CustomFieldType } from "@/lib/customFieldTypes";
import { cn } from "@/lib/utils";
import type { MappingSchema } from "./customFieldMappingHelpers";
import {
  OPPORTUNITY_MAPPABLE_FIELDS,
  type ERPSchemaDefinition,
  type ConnectionCapabilities,
  type ERPProviderKey,
  type OpportunityMappableFieldType,
} from "@forgepoint/shared";

/** Maps shared opportunity field types to UI custom field types. */
const FIELD_TYPE_MAP: Record<OpportunityMappableFieldType, CustomFieldType> = {
  user: "user",
  users: "multiUser",
  amount: "number",
  percentage: "percentage",
  date: "date",
};

interface OpportunityFieldMappingSectionProps {
  /** Current opportunityFieldMapping from the form config */
  mapping: Record<string, string>;
  /** Called when a mapping changes */
  onMappingChange: (mapping: Record<string, string>) => void;
  /** The field mapping schema from the resolved config (has enums per field) */
  mappingSchema: MappingSchema | null;
  /** Capability flags derived from granted OAuth scopes; null for non-HubSpot providers. */
  capabilities?: ConnectionCapabilities | null;
  /** The active ERP provider ID. */
  providerId?: ERPProviderKey | null;
}

interface FieldEntry {
  key: string;
  label: string;
  fieldType: CustomFieldType;
  options: FieldOption[];
  /** Provider-supplied hint messages per ERP field, keyed by field API name */
  fieldHints: Record<string, string>;
}

/**
 * Renders a mapping row per built-in opportunity field, each with a grouped
 * dropdown of compatible HubSpot deal properties. Options and grouping are
 * derived from the schema's `enum`/`enumNames` (format: `[groupName] Label`).
 * For owner fields (Sales Rep, Estimators), shows an email confirmation dialog
 * on first mapping.
 * Hidden when the schema has no opportunity field mapping properties.
 * @param props - OpportunityFieldMappingSectionProps
 * @returns The mapping section element, or null when there are no fields
 */
export function OpportunityFieldMappingSection({
  mapping,
  onMappingChange,
  mappingSchema,
  capabilities,
}: OpportunityFieldMappingSectionProps) {
  const fieldEntries = useMemo(
    () => getFieldEntries(mappingSchema),
    [mappingSchema],
  );

  const mappedCount = fieldEntries.filter(
    (e) => (mapping[e.key] ?? "") !== "",
  ).length;

  const [pendingUserMapping, setPendingUserMapping] = useState<{
    fieldKey: string;
    fieldLabel: string;
    value: string;
  } | null>(null);

  const handleFieldSelect = useCallback(
    (key: string, value: string, fieldType: CustomFieldType, label: string) => {
      if (!value) {
        const { [key]: _, ...rest } = mapping;
        onMappingChange(rest);
        return;
      }

      const wasPreviouslyMapped = !!mapping[key];
      if (isOwnerFieldType(fieldType) && !wasPreviouslyMapped) {
        setPendingUserMapping({ fieldKey: key, fieldLabel: label, value });
        return;
      }

      onMappingChange({ ...mapping, [key]: value });
    },
    [mapping, onMappingChange],
  );

  const handleConfirmUserMapping = useCallback(() => {
    if (!pendingUserMapping) {
      return;
    }
    onMappingChange({
      ...mapping,
      [pendingUserMapping.fieldKey]: pendingUserMapping.value,
    });
    setPendingUserMapping(null);
  }, [pendingUserMapping, mapping, onMappingChange]);

  if (fieldEntries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 border-t pt-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Field Mapping
        </span>
        <span
          className={cn(
            "rounded-full border px-2 py-0.5 text-[11px] tabular-nums transition-colors",
            mappedCount === fieldEntries.length && mappedCount > 0
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "text-muted-foreground",
          )}
        >
          {mappedCount}/{fieldEntries.length}
        </span>
      </div>

      {fieldEntries.map((entry) => (
        <div key={entry.key} className="grid gap-1">
          <CustomFieldRow
            fieldKey={entry.key}
            label={entry.label}
            fieldType={entry.fieldType}
            options={entry.options}
            selectedValue={mapping[entry.key] ?? ""}
            isMapped={!!mapping[entry.key]}
            onSelect={(value) =>
              handleFieldSelect(entry.key, value, entry.fieldType, entry.label)
            }
            disabled={
              isOwnerFieldType(entry.fieldType) &&
              !!capabilities &&
              !capabilities.canResolveOwners
            }
          />
          {isOwnerFieldType(entry.fieldType) &&
            !!capabilities &&
            !capabilities.canResolveOwners && (
              <div className="flex animate-in fade-in duration-300 items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                <span>
                  Owner fields will not sync — the Owners (read) scope was not
                  granted.
                </span>
              </div>
            )}
          {mapping[entry.key] && entry.fieldHints[mapping[entry.key]] && (
            <div className="flex animate-in fade-in duration-300 items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{entry.fieldHints[mapping[entry.key]]}</span>
            </div>
          )}
        </div>
      ))}

      <AlertDialog
        open={pendingUserMapping !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingUserMapping(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>User field mapping</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  &ldquo;{pendingUserMapping?.fieldLabel}&rdquo; will sync by
                  matching email addresses between systems.
                </p>

                <div className="flex items-center gap-3 rounded-md border bg-muted/50 px-3 py-2.5 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
                    <span className="font-medium text-foreground">
                      Forgepoint user email
                    </span>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
                    <span className="font-medium text-foreground">
                      HubSpot owner email
                    </span>
                  </div>
                </div>

                <p className="flex items-start gap-1.5 text-xs">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  If no HubSpot owner matches the email, this field will be
                  skipped during sync.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUserMapping}>
              Confirm mapping
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Extracts field entries from the opportunity field mapping schema. Each
 * entry's options are parsed from `enum`/`enumNames`, with the group name
 * extracted from the `[groupName] Label` format.
 * @param mappingSchema - The opportunityFieldMapping sub-schema
 * @returns Ordered field entries with parsed options
 */
function getFieldEntries(mappingSchema: MappingSchema | null): FieldEntry[] {
  if (!mappingSchema?.properties) {
    return [];
  }

  const { properties } = mappingSchema;
  const propertyOrder = Array.isArray(mappingSchema.propertyOrder)
    ? mappingSchema.propertyOrder
    : Object.keys(properties);

  return propertyOrder
    .filter((key) => properties[key] !== undefined)
    .map((key) => {
      const propDef = properties[key];
      const sharedDef = OPPORTUNITY_MAPPABLE_FIELDS[key];
      const label =
        sharedDef?.label ??
        (typeof propDef !== "boolean" ? propDef.title : undefined) ??
        key;
      const fieldType: CustomFieldType = sharedDef
        ? FIELD_TYPE_MAP[sharedDef.type]
        : "string";

      if (typeof propDef === "boolean") {
        return { key, label, fieldType, options: [], fieldHints: {} };
      }

      return {
        key,
        label,
        fieldType,
        options: parseFieldOptions(propDef),
        fieldHints: propDef.fieldHints ?? {},
      };
    });
}

/**
 * Parses a schema property's `enum`/`enumNames` into structured options.
 * The `enumNames` format is `[groupName] Label`; if the bracket prefix is
 * missing the full string is used as the label with an empty group.
 * @param propDef - The JSON schema definition for the field
 * @returns Parsed options with value, label, and groupName
 */
function parseFieldOptions(propDef: ERPSchemaDefinition): FieldOption[] {
  if (typeof propDef === "boolean" || !Array.isArray(propDef.enum)) {
    return [];
  }

  const enumNames: string[] | undefined = Array.isArray(propDef.enumNames)
    ? propDef.enumNames
    : undefined;

  return propDef.enum
    .filter((v): v is string => typeof v === "string")
    .map((value, index) => {
      const rawLabel = enumNames?.[index] ?? value;
      const match = rawLabel.match(/^\[(.+?)\]\s*(.+)$/);
      return {
        value,
        label: match ? match[2] : rawLabel,
        groupName: match ? match[1] : "",
      };
    });
}
